/**
 * Workspace V2 Route
 *
 * Haupt-Route für den Workspace mit Sub-Routes für verschiedene Modi.
 *
 * Routing-Struktur:
 * /v2/tools
 * ├── /                    → Redirect zu /generate/single
 * ├── /generate
 * │   ├── /single          → Einzelbild-Generierung
 * │   └── /batch           → Batch-Generierung (-30% Rabatt)
 * ├── /gallery             → Bildergalerie
 * ├── /convert             → Format-Konvertierung
 * ├── /aspect-ratio        → Seitenverhältnis ändern
 * ├── /upscale             → Hochskalierung
 * ├── /image-editor        → Unified Image Editor (Asset-Auswahl)
 * ├── /content-studio      → AI Content Studio (Social Media aus URLs)
 * ├── /price-calculator    → Preis Kalkulator für eCommerce
 * └── /bg-remove           → Hintergrund entfernen (Zukunft)
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { ToolsLayout } from './ToolsLayout';
import { SingleImageMode, SingleImageModeV2, StudioSingleShot, StudioVariations, BatchImageMode } from './generate';
import { ConvertMode, AspectRatioMode, UpscaleMode, KeywordByteCounter, AmazonTitleChecker } from '@/components/v2/tools/tools';
import { ImageEditorPage } from './image-editor';
import { FabricTest } from './image-editor/FabricTest';
import { GalleryV2Layout } from '@/components/v2/tools/gallery-v2';
import { ReviewMode } from './gallery/ReviewMode';
import { ImageAnalysisV2 } from '@/components/v2/tools/image-analysis';
import { TextToolsHub, TextToolPage, TextVorlagePage } from '@/components/v2/tools/text';
import { BulkTextToolPage, TextRunDetailPage } from '@/components/v2/tools/text-bulk';
import { ContentStudio } from './content-studio';
import { PriceCalculator } from '@/routes/v2/price-calculator';
import { ImageOptimize } from './image-optimize';
import { ChatImageGeneration } from '@/components/v2/tools/chat-image-generation';
import { AmazonProductOverview } from '@/components/v2/tools/amazon-listing';
import { AmazonListingPage } from '@/components/v2/tools/amazon-listing/AmazonListingPage';
import { ListingBuilderOverview, ListingBuilderDetail } from '@/components/v2/tools/listing-builder';
import { AmazonCatalogPage } from './AmazonCatalogPage';
import { EmptyState } from '@/components/v2/ui';
import { Construction, Eraser } from 'lucide-react';

/**
 * Placeholder für zukünftige Features
 */
function FuturePlaceholder({ title, icon: _Icon }: { title: string; icon: React.ElementType }) {
  return (
    <div className="flex items-center justify-center h-full p-8">
      <EmptyState
        icon={<Construction className="h-12 w-12" />}
        title={title}
        description="Dieses Feature ist noch in Entwicklung und wird bald verfügbar sein."
      />
    </div>
  );
}

export function Tools() {
  return (
    <Routes>
      <Route element={<ToolsLayout />}>
        {/* Default: Redirect zu Studio */}
        <Route index element={<Navigate to="studio/single-shot" replace />} />

        {/* Studio Routes (Generation UI) */}
        <Route path="studio">
          <Route index element={<Navigate to="single-shot" replace />} />
          <Route path="single-shot" element={<StudioSingleShot />} />
          <Route path="variations" element={<StudioVariations />} />
        </Route>

        {/* Generate Routes (Legacy) */}
        <Route path="generate">
          <Route index element={<Navigate to="single" replace />} />
          <Route path="single" element={<SingleImageMode />} />
          <Route path="single-v2" element={<SingleImageModeV2 />} />
          {/* Redirect old experimental to new studio */}
          <Route path="experimental" element={<Navigate to="/v2/tools/studio/single-shot" replace />} />
          <Route path="batch" element={<BatchImageMode />} />
          {/* Redirect old bulk to batch */}
          <Route path="bulk" element={<Navigate to="/v2/tools/generate/batch" replace />} />
        </Route>

        {/* Gallery V2 */}
        <Route path="gallery" element={<GalleryV2Layout />} />

        {/* Gallery Review Mode (Tinder-style approval) */}
        <Route path="gallery/review" element={<ReviewMode />} />

        {/* Convert */}
        <Route path="convert" element={<ConvertMode />} />

        {/* Aspect Ratio */}
        <Route path="aspect-ratio" element={<AspectRatioMode />} />

        {/* Upscale */}
        <Route path="upscale" element={<UpscaleMode />} />

        {/* Image Analysis (Gemini Vision) */}
        <Route path="image-analysis" element={<ImageAnalysisV2 />} />

        {/* Image Optimize (Bild optimieren) */}
        <Route path="image-optimize" element={<ImageOptimize />} />

        {/* Text Tools Hub + Individual Tool Pages */}
        <Route path="text">
          <Route index element={<TextToolsHub />} />
          {/* Bulk-Texte — eigenständiges Tool (T6b/E1b/E2b), muss vor :toolCode kommen */}
          <Route path="bulk" element={<BulkTextToolPage />} />
          <Route path="bulk/runs/:runId" element={<TextRunDetailPage />} />
          {/* Workspace-Vorlage detail page (must come before :toolCode catch) */}
          <Route path="vorlage/:templateId" element={<TextVorlagePage />} />
          <Route path=":toolCode" element={<TextToolPage />} />
        </Route>

        {/* Legacy: Text Generation redirect to new hub */}
        <Route path="text-generation" element={<Navigate to="/v2/tools/text" replace />} />

        {/* Content Studio (Social Media Content from URLs) */}
        <Route path="content-studio" element={<ContentStudio />} />

        {/* Image Editor - Vertex AI Image Editing Suite */}
        <Route path="image-editor" element={<ImageEditorPage />} />
        <Route path="fabric-test" element={<FabricTest />} />

        {/* Price Calculator - eCommerce Tool */}
        <Route path="price-calculator" element={<PriceCalculator />} />

        {/* Keyword Byte Counter - Amazon Backend Keywords Tool */}
        <Route path="keyword-byte-counter" element={<KeywordByteCounter />} />

        {/* Amazon Title Checker - Product Title Validation Tool */}
        <Route path="amazon-title-checker" element={<AmazonTitleChecker />} />

        {/* Product Listing Audit */}
        <Route path="product-listing-audit" element={<AmazonProductOverview />} />
        <Route path="product-listing-audit/:productId" element={<AmazonListingPage />} />

        {/* Listing Builder (Multi-Marktplatz Audit + Remake, Stufe 5) */}
        <Route path="listing-builder" element={<ListingBuilderOverview />} />
        <Route path="listing-builder/:jobId" element={<ListingBuilderDetail />} />
        {/* Legacy redirect */}
        <Route path="amazon-listing" element={<Navigate to="/v2/tools/product-listing-audit" replace />} />
        <Route path="amazon-listing/:productId" element={<Navigate to="/v2/tools/product-listing-audit" replace />} />

        {/* Amazon Catalog Lookup (Admin-only feature flag) */}
        <Route path="amazon-catalog" element={<AmazonCatalogPage />} />

        {/* Chat Image Generation (Conversational AI Image) */}
        <Route path="chat-image" element={<ChatImageGeneration />} />

        {/* Background Remove - Future Feature */}
        <Route path="bg-remove" element={<FuturePlaceholder title="Hintergrund entfernen" icon={Eraser} />} />

        {/* Catch-all: Redirect zu Studio (absoluter Pfad verhindert Endlosschleife) */}
        <Route path="*" element={<Navigate to="/v2/tools/studio/single-shot" replace />} />
      </Route>
    </Routes>
  );
}

export default Tools;
